<script setup lang="ts">
import { HeadObject, useHead } from '@vueuse/head';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, unref, useTemplateRef } from 'vue';
import { RouteLocationNormalized, useRoute, useRouter } from 'vue-router';

import Breadcrumb from '@/components/base/layout/Breadcrumb.vue';
import CategoryCard from '@/components/base/layout/CategoryCard.vue';
import Container from '@/components/base/layout/Container.vue';
import ColorGrid from '@/components/category/ColorGrid.vue';
import CategoryDescription from '@/components/category/Description.vue';
import CategoryDescriptionGlutenFree from '@/components/category/DescriptionGlutenFree.vue';
import { useRouterLinks } from '@/composables/navigation/useRouterLinks';
import { useCategory } from '@/stores/category';
import type { CategoryCard as CategoryCardModel } from '@/utils/category';

interface AncestorBreadcrumb {
  name: string;
  url?: string;
}
interface Props {
  ancestors?: AncestorBreadcrumb[];
  categoryKey: string;
  metaData?: Partial<HeadObject>;
  name?: string;
  presetFilters?: string[];
  topContent?: string;
  urlPrefix?: string;
}
const props = withDefaults(defineProps<Props>(), {
  ancestors: () => [],
  metaData: () => ({}),
  name: '',
  presetFilters: () => [],
  topContent: '',
  urlPrefix: '',
});

const route = useRoute();

const { category, entryCountsPerSubcategory, subcategories, updating } = storeToRefs(useCategory());
const mountedClass = ref('');
const description = computed(() => category.value.description);
const candyByColor = computed(() => route.path.includes('colors'));

useHead(
  computed(() => {
    if (!candyByColor.value) return props.metaData;
    return {
      ...props.metaData,
      link: unref(props.metaData?.link ?? []).concat([
        { href: nutshell['css/candy-by-color.css'], rel: 'stylesheet' },
      ]),
    };
  }),
);

const isGlutenFree = computed(
  () => props.ancestors.filter((item) => item.url === '/gluten-free').length > 0,
);

const activeClasses = computed(() => {
  const classes = [mountedClass.value];
  if (updating.value) classes.push('page-fade-manual-transition page-fade-enter');
  return classes.filter(Boolean).join(' ');
});

// essentially strips away the only breadcrumb without a `url` value; but cannot be fully trusted;
// TS rightfully knows that this does not conform with the `<Breadcrumb>` component's `ancestors` prop
const breadcrumbs = computed(() => props.ancestors.slice(0, -1));
const cardList = computed(() =>
  subcategories.value
    .filter((x) => x.objectID in entryCountsPerSubcategory.value)
    .map<CategoryCardModel>((sub) => ({
      ...sub,
      image: sub.image_url,
      path: `${props.urlPrefix}${sub.path}`,
      total:
        sub.type === 'synthesizedDepartment'
          ? sub.total
          : entryCountsPerSubcategory.value[sub.objectID],
    })),
);

const descriptionComponent = computed(() =>
  isGlutenFree.value ? CategoryDescriptionGlutenFree : CategoryDescription,
);

onMounted(() => {
  window.dataLayer.push({ GDR: { pagetype: 'department' } });
  mountedClass.value = 'mounted';
});

defineOptions({
  async criticalData(_, to: RouteLocationNormalized) {
    const [matched] = to.matched;
    const incomingProps = matched.props.default as Props;
    const algoliaProductFilters = incomingProps.presetFilters?.join(' AND ') || undefined;

    const categoryStore = useCategory();

    await Promise.all([
      categoryStore.loadCategory(incomingProps.categoryKey),
      categoryStore.loadEntryCountsPerSubcategory({
        analyticsTags: ['CLP', incomingProps.categoryKey, `CLP : ${incomingProps.name}`],
        categoryKey: incomingProps.categoryKey,
        filters: algoliaProductFilters,
      }),
      categoryStore.loadSubcategories({
        analyticsTags: ['CLP', incomingProps.categoryKey, `CLP : ${incomingProps.name}`],
        categoryKey: incomingProps.categoryKey,
        clickAnalytics: true,
        ruleContexts: [incomingProps.categoryKey],
      }),
    ]);
  },
});

const descriptionRef = useTemplateRef('description');
const topContentRef = useTemplateRef('topContent');
const router = useRouter();
useRouterLinks(descriptionRef, router);
useRouterLinks(topContentRef, router);
</script>

<template>
  <Container class="pt-4 lg:pt-6 page-content" data-test="clp-view" :class="activeClasses">
    <div>
      <div class="px-4 w-full">
        <Breadcrumb :ancestors="breadcrumbs" class="pb-1 ws-md:pb-5" />
      </div>
    </div>

    <div id="dy-hook-product-grid" v-once />

    <h1 class="px-4">{{ name }}</h1>

    <div v-html="topContent || ''" ref="topContent" />
    <ColorGrid v-if="candyByColor" />
    <div class="w-full sm:px-4 lg:px-10 lg:max-w-screen-2xl">
      <div class="flex-row w-full wrapped gutters ruled" data-test="clp">
        <div
          class="flex px-4 w-1/2 border-b border-solid ws-md:w-1/3 ws-lg:w-1/4 border-neutral-200"
          v-for="(card, index) in cardList"
          :key="card.name"
        >
          <CategoryCard :card="card" :position="index + 1" />
        </div>
      </div>
    </div>
    <Component
      v-if="description"
      :description="description"
      :is="descriptionComponent"
      class="px-4 pt-5 description"
      data-test="bottom-description"
      ref="description"
    />
  </Container>
</template>

<style lang="scss" scoped>
.page-content {
  &:deep(h3) {
    margin-bottom: 16px;
  }
  &:deep(.ptn-item-teaser) {
    h4 {
      font-size: 16px;
    }
  }
}
</style>
